import React, { useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Link,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import ManageSearchRoundedIcon from '@mui/icons-material/ManageSearchRounded';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import SpaRoundedIcon from '@mui/icons-material/SpaRounded';
import SummarizeRoundedIcon from '@mui/icons-material/SummarizeRounded';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Sidebar } from './Sidebar';

const sidebarCollapsedStorageKey = 'suite-flujos-sidebar-collapsed';

const getStoredSidebarCollapsed = () =>
    window.localStorage.getItem(sidebarCollapsedStorageKey) === 'true';

const getActiveHeaderItem = (pathname) => {
    if (pathname === '/' || pathname.startsWith('/admin')) {
        return { label: pathname.startsWith('/admin') ? 'Panel administrador' : 'Inicio', icon: <DashboardRoundedIcon /> };
    }

    if (pathname === '/entradas' || pathname === '/apps/flujos-fruta') {
        return { label: 'Flujos de fruta', icon: <SpaRoundedIcon /> };
    }

    if (/^\/consulta-entradas\/[^/]+\/editar$/.test(pathname)) {
        return { label: 'Editar entrada', icon: <ManageSearchRoundedIcon /> };
    }

    if (pathname.startsWith('/consulta-entradas')) {
        return { label: 'Consulta de entradas', icon: <ManageSearchRoundedIcon /> };
    }

    if (pathname.startsWith('/comparativa-almacenes')) {
        return { label: 'Comparativa de almacenes', icon: <CompareArrowsRoundedIcon /> };
    }

    if (pathname.startsWith('/historico-variedad')) {
        return { label: 'Historico por variedad', icon: <CategoryRoundedIcon /> };
    }

    if (pathname.startsWith('/informes')) {
        return { label: 'Informes', icon: <SummarizeRoundedIcon /> };
    }

    if (pathname.startsWith('/configuracion')) {
        return { label: 'Configuracion', icon: <SettingsRoundedIcon /> };
    }

    return { label: 'Aplicaciones', icon: <SpaRoundedIcon /> };
};

export const SuiteShell = ({ children }) => {
  const { user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(getStoredSidebarCollapsed);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const activeItem = getActiveHeaderItem(location.pathname);

  const toggleSidebarCollapsed = () => {
        setIsSidebarCollapsed((collapsed) => {
            const nextCollapsed = !collapsed;
            window.localStorage.setItem(sidebarCollapsedStorageKey, String(nextCollapsed));
            return nextCollapsed;
        });
  };

  const handleLogout = async (event) => {
    event.preventDefault();
    setLogoutDialogOpen(true);
  };

  const confirmLogout = async () => {
    await logout();
    setLogoutDialogOpen(false);
    navigate('/login', { replace: true });
  };

  const displayName = user?.displayName || user?.username || 'Usuario';
  const avatarLetter = displayName.charAt(0).toUpperCase();

    return (
        <Box sx={{ height: '100dvh', bgcolor: 'background.default', display: 'flex', overflow: 'hidden' }}>
            <Sidebar
                open={isMobileMenuOpen}
                mobile={isMobile}
                collapsed={isSidebarCollapsed}
                onClose={() => setIsMobileMenuOpen(false)}
                onToggleCollapse={toggleSidebarCollapsed}
            />

            <Box sx={{ flex: 1, minWidth: 0, height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box
                    component="header"
                    sx={{
                        minHeight: { xs: 64, md: 78 },
                        px: { xs: 1.5, sm: 3, md: 4 },
                        py: { xs: 1, md: 1.25 },
                        bgcolor: 'rgba(255,255,255,0.92)',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        flex: '0 0 auto',
                        gap: { xs: 1, sm: 1.5, md: 2 },
                        flexWrap: 'nowrap',
                        zIndex: 10,
                        backdropFilter: 'blur(12px)',
                    }}
                >
                    <IconButton
                        aria-label="Abrir menu"
                        onClick={() => setIsMobileMenuOpen(true)}
                        sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Stack
                        direction="row"
                        spacing={1.25}
                        sx={{
                            alignItems: 'center',
                            minWidth: 0,
                            flex: '1 1 auto',
                        }}
                    >
                        <Box
                            sx={{
                                width: { xs: 38, sm: 40 },
                                height: { xs: 38, sm: 40 },
                                borderRadius: 1.5,
                                bgcolor: 'primary.container',
                                color: 'primary.main',
                                display: {xs: 'none', md: 'flex'},
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: '0 0 auto',
                                border: '1px solid',
                                borderColor: 'divider',
                            }}
                        >
                            {React.cloneElement(activeItem.icon, { fontSize: 'small' })}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    color: 'primary.main',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    lineHeight: '15px',
                                    textTransform: 'uppercase',
                                }}
                            >
                                Suite Flujos
                            </Typography>
                            <Typography
                                sx={{
                                    color: 'text.primary',
                                    fontSize: { xs: 16, sm: 17 },
                                    fontWeight: 700,
                                    lineHeight: '22px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {activeItem.label}
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack
                        direction="row"
                        spacing={1.25}
                        sx={{
                            alignItems: 'center',
                            ml: 'auto',
                            minWidth: 0,
                            flex: '0 0 auto',
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: { xs: 36, sm: 36 },
                                    height: { xs: 36, sm: 36 },
                                    bgcolor: 'primary.main',
                                    fontWeight: 700,
                                    fontSize: 18,
                                }}
              >
                {avatarLetter}
              </Avatar>
              <Box sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: '19px' }}>
                  {displayName}
                </Typography>
                <Link
                  href="#"
                  underline="none"
                  onClick={handleLogout}
                  sx={{
                                        color: 'text.secondary',
                                        display: 'inline-flex',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        lineHeight: '15px',
                                    }}
                                >
                                    Cerrar sesion
                                </Link>
                            </Box>
                        </Stack>
                    </Stack>
                </Box>

                <Box
                    component="main"
                    sx={{
                        flex: '1 1 auto',
                        minHeight: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        px: { xs: 2, sm: 3, md: 4 },
                        py: { xs: 2, sm: 1.5 },
                    }}
                >
                    {children}
                </Box>
            </Box>

            <Dialog
                open={logoutDialogOpen}
                onClose={() => setLogoutDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Cerrar sesion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Se cerrara tu sesion actual y volveras a la pantalla de login.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={() => setLogoutDialogOpen(false)}>
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={confirmLogout}>
                        Cerrar sesion
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
